﻿import "@fortawesome/fontawesome-free/css/all.min.css";
import {
	Box,
	Card,
	Table,
	TableBody,
	TableCell,
	TableRow,
	Typography,
} from "@mui/material";
import { M1ShiftStatsData } from "../models/M1Overview";

type Props = {
	title: string;
	data: M1ShiftStatsData;
	showTPHIndicator?: boolean;
};

const formatNumber = (
	value: number | null | undefined,
	decimals = 0,
): string => {
	if (value === undefined || value === null) return "-";
	return parseFloat(value.toFixed(decimals)).toLocaleString("en-US", {
		minimumFractionDigits: 0,
		maximumFractionDigits: decimals,
	});
};

const PerformanceArrow = ({
	percentage,
	isNegative,
}: {
	percentage: number;
	isNegative: boolean;
}) => {
	const color = isNegative ? "#E53935" : "#43A047";

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				gap: 1.5,
			}}
		>
			<Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color }}>
				{isNegative ? "-" : "+"}TPH
			</Typography>

			<Box
				component={"i"}
				sx={{ color }}
				className={
					isNegative
						? "fa-solid fa-arrow-trend-down fa-xl"
						: "fa-solid fa-arrow-trend-up fa-xl"
				}
			></Box>

			<Typography sx={{ fontSize: "1rem", fontWeight: 700, color }}>
				{Math.abs(percentage).toFixed(0)}%
			</Typography>
		</Box>
	);
};

export const M1ShiftStats = ({
	title,
	data,
	showTPHIndicator = false,
}: Props) => {
	const mainRows = [
		{ label: "Billet Tons", value: formatNumber(data.BilletTons, 2) },
		{ label: "Billet Pc's.", value: formatNumber(data.BilletPcs) },
		{ label: "Total Cobbles", value: "-" },
		{ label: "Total Adjustments", value: "-" },
	];

	const statsRows = [
		{
			label: "Production",
			value: `${formatNumber(data.Production, 2)}%`,
			indicator: "production",
		},
		{
			label: "Delays",
			value: `${formatNumber(data.Delays, 2)}%`,
			indicator: "delay",
		},
		{
			label: "Gap Time",
			value: `${formatNumber(data.GapTime, 2)}%`,
			indicator: "gaptime",
		},
		{
			label: "TPH",
			value: formatNumber(data.TPH, 4),
		},
		{
			label: "Target TPH",
			value: formatNumber(data.TargetTPH, 0),
		},
	];

	const tphDifference = data.TPHDifference;
	const isNegative = tphDifference < 0;

	return (
		<Box>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 1,
				}}
			>
				<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
					{title}
				</Typography>
			</Box>

			<Box display="flex" flexDirection="row" gap={1} alignItems="stretch">
				<Card variant="outlined" sx={{ flex: 1, p: 1 }}>
					<Table
						size="small"
						sx={{ "& td, & th": { py: 0.2, px: 0.5, borderBottom: "none" } }}
					>
						<TableBody>
							{mainRows.map((row) => (
								<TableRow key={row.label}>
									<TableCell sx={{ fontWeight: 500, fontSize: "0.7rem" }}>
										{row.label}
									</TableCell>
									<TableCell align="right" sx={{ fontSize: "0.7rem" }}>
										{row.value}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Card>

				<Card variant="outlined" sx={{ flex: 1, p: 1, alignItems: "center" }}>
					<Table
						size="small"
						sx={{ "& td, & th": { py: 0.2, px: 0.5, borderBottom: "none" } }}
					>
						<TableBody>
							{statsRows.map((row) => (
								<TableRow key={row.label}>
									<TableCell sx={{ fontSize: "0.7rem" }}>
										{row.indicator && (
											<Box
												component="span"
												sx={{
													display: "inline-block",
													width: 8,
													height: 8,
													borderRadius: "50%",
													backgroundColor:
														row.indicator === "production"
															? "#4CAF50"
															: row.indicator === "delay"
																? "#F44336"
																: "#9E9E9E",
													mr: 0.5,
													verticalAlign: "middle",
												}}
											/>
										)}
										{row.label}
									</TableCell>
									<TableCell align="right" sx={{ fontSize: "0.7rem" }}>
										{row.value}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Card>

				{showTPHIndicator && (
					<Box flex={0.4} alignItems="center">
						<PerformanceArrow
							percentage={tphDifference}
							isNegative={isNegative}
						/>
					</Box>
				)}
			</Box>
		</Box>
	);
};
